import { ReactNode, useEffect, useRef, useState } from "react";

/**
 * Defers mounting children until the placeholder is near the viewport.
 * Combined with React.lazy children, this means the chunk + its assets
 * are not fetched on initial homepage load, cutting unused JS.
 *
 * minHeight prevents layout shift while the section is unmounted.
 */
interface Props {
  children: ReactNode;
  minHeight: string;
  rootMargin?: string;
}

const DeferUntilVisible = ({ children, minHeight, rootMargin = "400px" }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show) return;
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setShow(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShow(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [show, rootMargin]);

  return (
    <div ref={ref} style={show ? undefined : { minHeight }}>
      {show ? children : null}
    </div>
  );
};

export default DeferUntilVisible;
